import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import "./login.css";
import { FaSpinner, FaEye, FaEyeSlash } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import NavbarLog from "../NavbarLog/NavbarLog";
import ChatBot from "../../Chatbot/chatbot";

const TOAST_OPTIONS = {
  position: "top-right",
  autoClose: 3000,
  hideProgressBar: false,
  style: {
    backgroundColor: "rgba(60, 60, 81, 0.4)",
    color: "white",
    fontSize: "13px",
    borderRadius: "8px",
    padding: "15px 20px",
    width: "160px",
    boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
  },
};

const Login = () => {
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    const storedEmail = localStorage.getItem("email");
    const storedPassword = localStorage.getItem("password");
    const storedRememberMe = localStorage.getItem("rememberMe");

    if (storedRememberMe === "true") {
      setEmail(storedEmail || "");
      setPassword(storedPassword || "");
      setRememberMe(true);
    }
  }, []);

  const handleEmailChange = (e) => setEmail(e.target.value);
  const handlePasswordChange = (e) => setPassword(e.target.value);
  const toggleShowPassword = () => setShowPassword(!showPassword);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/users/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(data.message || "Login successful!", TOAST_OPTIONS);

        // Store the token BEFORE any navigation, otherwise the next API calls fail with 401
        localStorage.setItem("token", data.token);
        localStorage.setItem("role", data.user.role);
        localStorage.setItem("userId", data.user.id);
        localStorage.setItem("userEmail", data.user.email);

        if (rememberMe) {
          localStorage.setItem("email", email);
          localStorage.setItem("password", password);
          localStorage.setItem("rememberMe", "true");
        } else {
          localStorage.removeItem("email");
          localStorage.removeItem("password");
          localStorage.setItem("rememberMe", "false");
        }

        // Redirect by role (no subscription check anymore)
        setTimeout(() => {
          if (data.user.role === "admin") {
            navigate("/dashboardAdmin");
          } else if (data.user.role === "commercial_admin") {
            navigate("/dashboardCommercial");
          } else {
            navigate("/dashboard");
          }
        }, 1000);
      } else {
        toast.error(data.message || "Login failed.", TOAST_OPTIONS);
      }
    } catch (error) {
      console.error(error);
      toast.error("Server error.", TOAST_OPTIONS);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container">
      <ToastContainer />
      <NavbarLog />
      <ChatBot mode="public" />
      <div className="wrapper" role="main" aria-label="Login form">
        <form onSubmit={handleSubmit} aria-labelledby="login-heading">
          <h1 id="login-heading">Login</h1>
          <div className="input-box">
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={handleEmailChange}
              required
              aria-label="Email address"
            />
          </div>

          <div className="input-box">
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              value={password}
              onChange={handlePasswordChange}
              required
              aria-label="Password"
            />
            <span
              onClick={toggleShowPassword}
              className="password-toggle-icon"
              aria-label={showPassword ? "Hide password" : "Show password"}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && toggleShowPassword()}
            >
              {showPassword ? <FaEyeSlash /> : <FaEye />}
            </span>
          </div>

          <div className="remember-forgot">
            <div className="form-group remember-forgot">
              <label>
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={() => setRememberMe(!rememberMe)}
                  aria-label="Remember me"
                />
                Remember me
              </label>
            </div>
          </div>

          <button
            type="submit"
            className="btn-login"
            disabled={isLoading}
            aria-label={isLoading ? "Logging in" : "Login"}
          >
            {isLoading ? (
              <FaSpinner className="spinner" aria-hidden="true" />
            ) : (
              "Login"
            )}
          </button>

          <div className="register-link">
            <p>
              Don't have an account?
              <Link
                to="/signup"
                className="text-primary text-gradient font-weight-bold"
                aria-label="Sign up"
              >
                Sign up
              </Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login;